import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: 'Excellence',
    title: 'Excellence',
    description: 'Nous preferons une execution nette, solide et durable a une promesse rapide mais superficielle.',
    promise: 'Vous recevez un niveau de finition coherent avec une marque premium.',
    details: [
      'Standards de qualite eleves',
      'Souci du detail sur le fond comme sur la forme',
      'Revision continue jusqu a la bonne version'
    ]
  },
  {
    icon: 'Collaboration',
    title: 'Collaboration',
    description: 'Nous travaillons avec vous comme un partenaire de confiance, pas comme un simple executant.',
    promise: 'Vous gardez de la visibilite, de la clarte et une vraie place dans les arbitrages.',
    details: [
      'Communication transparente',
      'Decisions partagees et documentees',
      'Ecoute active des contraintes terrain'
    ]
  },
  {
    icon: 'Innovation',
    title: 'Innovation',
    description: 'Nous utilisons la technologie comme un levier de positionnement, de marge et de simplification.',
    promise: 'L innovation reste utile, rentable et integree avec discernement.',
    details: [
      'Veille technologique continue',
      'IA et automatisation choisies avec exigence',
      'Solutions creatives orientees business'
    ]
  },
  {
    icon: 'Integrite',
    title: 'Integrite',
    description: 'Nous disons ce qui est pertinent, ce qui ne l est pas, et ce qu il faut vraiment prioriser.',
    promise: 'Vous evitez les detours couteux, les fausses bonnes idees et les zones floues.',
    details: [
      'Feedback honnete',
      'Transparence sur le scope et les limites',
      'Engagement professionnel constant'
    ]
  },
  {
    icon: 'Performance',
    title: 'Performance',
    description: 'Un beau projet ne suffit pas. Il doit soutenir vos ventes, votre organisation et votre croissance.',
    promise: 'Nous relions le design, la technique et l execution aux resultats concrets.',
    details: [
      'Objectifs mesurables',
      'Pilotage par la valeur creee',
      'Lecture ROI et impact operationnel'
    ]
  },
  {
    icon: 'Agilite',
    title: 'Agilite',
    description: 'Nous savons monter vite en execution sans perdre la rigueur du cadre initial.',
    promise: 'Vous gagnez en vitesse tout en gardant un cap clair.',
    details: [
      'Methodologie souple et maitrisee',
      'Adaptation rapide aux retours terrain',
      'Cadence de travail lisible'
    ]
  }
];

const collaborationStandards = [
  {
    title: 'Clarte',
    description: 'Un projet avance mieux quand les attentes, les priorites et les arbitrages sont explicites.'
  },
  {
    title: 'Responsabilite',
    description: 'Nous assumons la qualite de ce que nous livrons et la pertinence de ce que nous recommandons.'
  },
  {
    title: 'Elegance',
    description: 'Dans le design, dans le code et dans la communication, nous cherchons la justesse plus que l effet.'
  }
];

const Values = () => {
  return (
    <>
      <Helmet>
        <title>Nos Valeurs - Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Les valeurs qui guident Couteau Suisse Digital: excellence, integrite, collaboration, performance et execution premium."
        />
      </Helmet>

      <main>
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,106,0,0.22),_transparent_38%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-200 backdrop-blur"
                >
                  <span className="h-2 w-2 rounded-full bg-neon-orange" />
                  Notre cadre de travail
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 }}
                  className="mt-6 max-w-4xl text-4xl font-bold leading-tight text-white md:text-6xl"
                >
                  Des valeurs qui structurent
                  <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> une execution premium</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.16 }}
                  className="mt-6 max-w-3xl text-lg leading-8 text-gray-300 md:text-xl"
                >
                  Nous croyons qu une marque haut de gamme se construit autant par la qualite du livrable que par
                  la qualite de la relation, de la clarte et du jugement apporte tout au long du projet.
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.24 }}
                className="rounded-[30px] border border-white/10 bg-white/5 p-6 text-white backdrop-blur"
              >
                <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">En pratique</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-3">
                  {collaborationStandards.map((item) => (
                    <div key={item.title} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                      <h2 className="text-lg font-semibold">{item.title}</h2>
                      <p className="mt-3 text-sm leading-6 text-gray-300">{item.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-[#f8f6f2] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Ce que vous ressentez</p>
              <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Nos valeurs ne restent pas en slogan</h2>
              <p className="mt-5 text-base leading-8 text-gray-600">
                Elles se traduisent dans le rythme du projet, la qualite des recommandations, la precision du design,
                la proprete du code et la facon dont nous vous aidons a prendre de meilleures decisions.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="rounded-[30px] border border-gray-200 bg-white p-7 shadow-[0_22px_70px_rgba(17,24,39,0.06)]"
                >
                  <div className="inline-flex rounded-full border border-neon-orange/20 bg-neon-orange/10 px-4 py-1.5 text-sm font-medium text-neon-orange">
                    {value.icon}
                  </div>
                  <h3 className="mt-5 text-2xl font-bold text-gray-900">{value.title}</h3>
                  <p className="mt-4 text-base leading-7 text-gray-700">{value.description}</p>

                  <div className="mt-5 rounded-2xl bg-[#f8f6f2] p-4">
                    <p className="text-sm font-medium uppercase tracking-[0.18em] text-gray-500">Pour vous</p>
                    <p className="mt-2 text-sm leading-6 text-gray-700">{value.promise}</p>
                  </div>

                  <div className="mt-6 space-y-3">
                    {value.details.map((detail) => (
                      <div key={detail} className="flex items-start gap-3">
                        <div className="mt-1 h-2.5 w-2.5 rounded-full bg-neon-orange" />
                        <p className="text-sm leading-6 text-gray-600">{detail}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-18 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="rounded-[30px] bg-gray-950 p-7 text-white"
              >
                <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Pourquoi cela compte</p>
                <h2 className="mt-4 text-3xl font-bold">Un bon projet change aussi la perception de votre marque</h2>
                <p className="mt-5 text-base leading-8 text-gray-300">
                  Quand les standards internes sont eleves, cela se voit partout: dans la confiance que le site inspire,
                  dans la precision du discours, dans la fluidite des operations et dans la facon dont vos prospects
                  evaluent votre niveau.
                </p>
              </motion.div>

              <div className="grid gap-4 md:grid-cols-3">
                {[
                  'Des arbitrages plus clairs',
                  'Une execution plus soignee',
                  'Une relation de travail plus sereine'
                ].map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="rounded-[26px] border border-gray-200 bg-[#fbfaf8] p-6"
                  >
                    <div className="text-sm uppercase tracking-[0.18em] text-neon-orange">CSD</div>
                    <p className="mt-4 text-lg font-semibold text-gray-900">{item}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,106,0,0.16),_transparent_40%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-white md:text-5xl"
            >
              Si cette maniere de travailler vous parle,
              <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> construisons quelque chose de fort</span>
            </motion.h2>

            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-300">
              Nous aimons les projets ambitieux, bien cadres et portes par des entreprises qui veulent monter en gamme
              avec exigence.
            </p>

            <Link
              to="/reserver"
              className="mt-10 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-neon-orange/20"
            >
              Discuter de votre projet
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default Values;
